/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { toast } from "react-hot-toast";
import { BsDownload, BsCheckCircle } from "react-icons/bs";

import { esEquipoInterno } from "../../constants/equipoInterno";
import { useAuthContext } from "../../context/auth";
import { getClientes, getEdificios, getIntermediarios, getOportunidades } from "../../utils";
import { getAllCotizacionesPais, getUsuariosConAgencia } from "../../utils/cotizacionesGuardadas";
import { formalizarCotizacion } from "../../utils/formalizarCotizacion";
import { computePulsoRegional } from "../../utils/pulsoRegional";
import { useExchangeRates } from "../../utils/useExchangeRates";
import { AgenciasDropdown, matchAgenciaFilter } from "../AgenciasDropdown";
import { Header } from "../Header";
import NotificacionesCotizacion from "../NotificacionesCotizacion";
import PdfCotizacion from "../PdfCotizacion";
import PulsoRegional from "../PulsoRegional";
import FormalizarModal from "./FormalizarModal";
import { pdf } from "@react-pdf/renderer";
import styles from "./PropuestasExternas.module.scss";

const PropuestasExternas = () => {
  const { pais, rol, session, id } = useAuthContext();
  const otroPais = pais === "Chile" ? "Perú" : "Chile";
  const { dolarFallback } = useExchangeRates(pais);

  // =====================================================
  // STATE
  // =====================================================
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [clientes, setClientes] = useState([]);

  // Filtros
  const [busqueda, setBusqueda] = useState("");
  const [filtroAgencia, setFiltroAgencia] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortAsc, setSortAsc] = useState(false);

  // Modal formalizar
  const [formalizarTarget, setFormalizarTarget] = useState(null);

  // Sección propuestas locales
  const [localOpen, setLocalOpen] = useState(true);

  // Pulso Regional
  const [pulsoAlerts, setPulsoAlerts] = useState([]);
  const [pulsoLoading, setPulsoLoading] = useState(false);

  // Edificios para tabla de inventario del PDF
  const [edificiosData, setEdificiosData] = useState([]);

  // =====================================================
  // FORMATO MONEDA
  // =====================================================
  const formatNum = useCallback(
    (amount) => {
      const num = Number(amount) || 0;
      const locale = pais === "Chile" ? "es-CL" : "es-PE";
      return num.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    [pais]
  );

  const monedaSymbol = pais === "Chile" ? "$" : "S/";

  // =====================================================
  // FETCH DATA
  // =====================================================
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cotData, usrData, intData, cliData] = await Promise.all([
        getAllCotizacionesPais(pais),
        getUsuariosConAgencia(pais),
        getIntermediarios(pais),
        getClientes({ pais }),
      ]);
      setCotizaciones(cotData || []);
      setUsuarios(usrData || []);
      setIntermediarios(intData || []);
      setClientes(cliData || []);
    } catch (err) {
      console.error("Error cargando propuestas externas:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [pais]);

  useEffect(() => {
    if (pais) fetchData();
  }, [pais, fetchData]);

  // Cargar edificios del país (para tabla de inventario del PDF)
  useEffect(() => {
    if (!pais) return;
    const fetchEdificios = async () => {
      try {
        const data = await getEdificios({ pais });
        setEdificiosData(data || []);
      } catch (err) {
        console.error("Error cargando edificios:", err);
        setEdificiosData([]);
      }
    };
    fetchEdificios();
  }, [pais]);

  // Fetch Pulso Regional (datos del otro país)
  useEffect(() => {
    if (!pais || pais === "Region") return;
    const currentYear = new Date().getFullYear();

    const fetchPulso = async () => {
      setPulsoLoading(true);
      try {
        const [clientesOtroPais, salesOtroPais] = await Promise.all([
          getClientes({ pais: otroPais }),
          getOportunidades({ pais: otroPais, ano: currentYear }),
        ]);
        const alerts = computePulsoRegional({
          miPais: pais,
          clientesOtroPais,
          salesOtroPais,
        });
        setPulsoAlerts(alerts);
      } catch (err) {
        console.error("Error cargando pulso regional:", err);
      } finally {
        setPulsoLoading(false);
      }
    };
    fetchPulso();
  }, [pais]);

  // =====================================================
  // MAPAS DE LOOKUP
  // =====================================================
  const usuariosMap = useMemo(() => {
    const map = {};
    for (const u of usuarios) {
      map[u.id] = u;
    }
    return map;
  }, [usuarios]);

  const intermediariosMap = useMemo(() => {
    const map = {};
    for (const i of intermediarios) {
      map[i.id] = i;
    }
    return map;
  }, [intermediarios]);

  // =====================================================
  // ENRIQUECER COTIZACIONES
  // =====================================================
  const cotizacionesEnriquecidas = useMemo(
    () =>
      cotizaciones
        .filter((c) => {
          const user = usuariosMap[c.usuario_id];
          const rol = user?.rol || "";
          return rol === "agencia" || rol === "cliente";
        })
        .map((c) => {
          const user = usuariosMap[c.usuario_id];
          const agenciaId = user?.agencia || null;
          const agencia = agenciaId ? intermediariosMap[agenciaId] : null;
          return {
            ...c,
            _agencia_id: agenciaId,
            _agencia_nombre: agencia?.nombre || "-",
            _ejecutivo_nombre: user ? `${user.nombre || ""} ${user.apellido || ""}`.trim() : "-",
            _rol_creador: user?.rol || "cliente",
          };
        }),
    [cotizaciones, usuariosMap, intermediariosMap]
  );

  const borradorCount = useMemo(
    () => cotizacionesEnriquecidas.filter((c) => c.estado === "borrador").length,
    [cotizacionesEnriquecidas]
  );

  // =====================================================
  // BUCKET "NUEVAS COTIZACIONES" — solo agencias/clientes REALES del propio país.
  // Excluye al equipo interno (regla 10.7, por ID): un interno con rol temporal
  // 'agencia'/'cliente' no debe colarse como si fuera una agencia real. No afecta
  // a "Propuestas Agencias" (esa tabla sigue usando `cotizacionesEnriquecidas`).
  // =====================================================
  const cotizacionesNotificaciones = useMemo(
    () => cotizacionesEnriquecidas.filter((c) => !esEquipoInterno(c.usuario_id)),
    [cotizacionesEnriquecidas]
  );

  // =====================================================
  // FILTRADO Y ORDENAMIENTO
  // =====================================================
  const cotizacionesFiltradas = useMemo(() => {
    let filtered = [...cotizacionesEnriquecidas];

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.codigo_generado || "").toLowerCase().includes(q) ||
          (c.nombre_cotizacion || "").toLowerCase().includes(q) ||
          (c.cliente_nombre || "").toLowerCase().includes(q) ||
          (c._agencia_nombre || "").toLowerCase().includes(q) ||
          (c._ejecutivo_nombre || "").toLowerCase().includes(q)
      );
    }

    if (filtroAgencia) {
      filtered = filtered.filter((c) => matchAgenciaFilter(filtroAgencia, c._agencia_id, pais));
    }

    if (filtroEstado) {
      filtered = filtered.filter((c) => c.estado === filtroEstado);
    }

    filtered.sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];

      // Campos enriquecidos
      if (sortColumn === "_agencia_nombre" || sortColumn === "_ejecutivo_nombre") {
        valA = (a[sortColumn] || "").toLowerCase();
        valB = (b[sortColumn] || "").toLowerCase();
      } else if (
        sortColumn === "inversion_final_total" ||
        sortColumn === "version" ||
        sortColumn === "edificios_unicos_total" ||
        sortColumn === "ascensores_seleccionados"
      ) {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
      } else if (sortColumn === "created_at") {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      } else {
        valA = (valA || "").toString().toLowerCase();
        valB = (valB || "").toString().toLowerCase();
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [cotizacionesEnriquecidas, busqueda, filtroAgencia, filtroEstado, sortColumn, sortAsc, pais]);

  // =====================================================
  // HANDLERS
  // =====================================================
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(column);
      setSortAsc(true);
    }
  };

  const sortIndicator = (column) => {
    if (sortColumn !== column) return "";
    return sortAsc ? " \u25B2" : " \u25BC";
  };

  const handleDownloadPdf = async (c) => {
    try {
      toast.loading("Generando PDF...", { id: "pdf-gen" });
      const moneda = c.moneda_dolar ? "US$" : pais === "Chile" ? "$" : "S/";
      const impactosEfectivosPoisson = {
        ascensores: c.impactos_efectivos_ascensores || 0,
        totems: c.impactos_efectivos_totems || 0,
        cowork: c.impactos_efectivos_cowork || 0,
        espera: c.impactos_efectivos_espera || 0,
        total: c.impactos_efectivos_total || 0,
      };
      const splitNames = (str) => (str ? str.split(",") : []);
      const doc = (
        <PdfCotizacion
          pais={c.pais || pais}
          cliente={c.cliente_nombre || ""}
          campana={c.nombre_cotizacion || ""}
          codigoGenerado={c.codigo_generado}
          usuarioNombre={c.usuario_nombre || ""}
          usuarioApellido={c.usuario_apellido || ""}
          selectedMoneda={moneda}
          isProgrammaticSelected={c.is_programmatic_selected || false}
          includeInstallingBuildings={false}
          plazo={c.plazo || 0}
          frecuencia={c.frecuencia || 0}
          duracion={c.duracion || 0}
          horasFinal={c.horas_final || c.horas || 0}
          intervalos={c.intervalos || []}
          options={c.options || []}
          ascensoresSeleccionados={c.ascensores_seleccionados || 0}
          totemsSeleccionados={c.totems_seleccionados || 0}
          coworkSeleccionados={c.cowork_seleccionados || 0}
          esperaSeleccionados={c.espera_seleccionados || 0}
          audienciaSeleccionadaTotal={c.audiencia_seleccionada_total || 0}
          audienciaSeleccionadaAscensor={c.audiencia_seleccionada_ascensor || 0}
          audienciaSeleccionadaTotems={c.audiencia_seleccionada_totems || 0}
          audienciaSeleccionadaCowork={c.audiencia_seleccionada_cowork || 0}
          audienciaSeleccionadaEspera={c.audiencia_seleccionada_espera || 0}
          salidasAscensores={c.salidas_ascensores || 0}
          salidasTotems={c.salidas_totems || 0}
          salidasCowork={c.salidas_cowork || 0}
          salidasEspera={c.salidas_espera || 0}
          salidasTotales={c.salidas_totales || 0}
          impactosTotalAscensores={c.impactos_total_ascensores || 0}
          impactosTotalTotems={c.impactos_total_totems || 0}
          impactosTotalCowork={c.impactos_total_cowork || 0}
          impactosTotalEspera={c.impactos_total_espera || 0}
          impactosTotales={c.impactos_totales || 0}
          impactosEfectivosPoisson={impactosEfectivosPoisson}
          contactosCampañaAscensores={c.contactos_campana_ascensores || 0}
          contactosCampañaTotems={c.contactos_campana_totems || 0}
          contactosCampañaCowork={c.contactos_campana_cowork || 0}
          contactosCampañaEspera={c.contactos_campana_espera || 0}
          inversionAscensoresFinal={c.inversion_ascensores_final || 0}
          inversionTotemsFinal={c.inversion_totems_final || 0}
          inversionCoworkFinal={c.inversion_cowork_final || 0}
          inversionEsperaFinal={c.inversion_espera_final || 0}
          inversionFinalTotal={c.inversion_final_total || 0}
          tarifaFriaAscensores={c.tarifa_fria_ascensores || 0}
          tarifaFriaTotems={c.tarifa_fria_totems || 0}
          tarifaFriaCowork={c.tarifa_fria_cowork || 0}
          tarifaFriaEspera={c.tarifa_fria_espera || 0}
          tarifaFriaTotal={c.tarifa_fria_total || 0}
          cpmAscensoresFinal={c.cpm_ascensores_final || 0}
          cpmTotemsFinal={c.cpm_totems_final || 0}
          cpmCoworkFinal={c.cpm_cowork_final || 0}
          cpmEsperaFinal={c.cpm_espera_final || 0}
          cpmTotalFinal={c.cpm_total_final || 0}
          descuentoGlobal={c.descuento_total_final || 0}
          manualDiscountAmount={c.manual_discount_amount || null}
          manualDiscountPercentage={c.discount_percentage || null}
          finalAmount={c.final_amount || null}
          nombresEdificiosSeleccionadosAscensores={splitNames(c.nombres_edificios_seleccionados_ascensores)}
          nombresEdificiosSeleccionadosTotems={splitNames(c.nombres_edificios_seleccionados_totems)}
          nombresEdificiosSeleccionadosCowork={splitNames(c.nombres_edificios_cowork)}
          nombresEdificiosSeleccionadosEspera={splitNames(c.nombres_edificios_espera)}
          nombresEdificiosNoSeleccionados={[]}
          edificios={edificiosData}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Cotizacion_${c.codigo_generado}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado", { id: "pdf-gen" });
    } catch (err) {
      console.error("Error generando PDF:", err);
      toast.error("Error al generar PDF", { id: "pdf-gen" });
    }
  };

  const handleFormalizar = async (cotizacion, formData) => {
    await formalizarCotizacion(cotizacion, formData, { rol, userId: id });
    toast.success("Propuesta formalizada correctamente");
    setFormalizarTarget(null);
    fetchData();
  };

  const formatFecha = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getConfianzaClass = (confianza) => {
    if (!confianza) return "";
    const num = confianza.replace("%", "");
    return styles[`confianza_${num}`] || "";
  };

  // =====================================================
  // RENDER
  // =====================================================
  if (loading) {
    return (
      <div className={styles.wrapper}>
        <Header leftContent={<h2 className={styles.titulo}>{`${otroPais} & Agencias`}</h2>} />
        <div className={styles.content}>
          <div className={styles.empty}>Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Header leftContent={<h2 className={styles.titulo}>{`${otroPais} & Agencias`}</h2>} />
      <div className={styles.content}>
        {/* ─── NOTIFICACIONES COTIZACIONES NUEVAS ─── */}
        <NotificacionesCotizacion cotizaciones={cotizacionesNotificaciones} loading={loading} rol={rol} />

        {/* ─── PULSO REGIONAL ─── */}
        <PulsoRegional
          alerts={pulsoAlerts}
          loading={pulsoLoading}
          rol={rol}
          dolarOtroPais={dolarFallback?.[otroPais] || 0}
          usuarioId={session?.user?.id || ""}
        />

        {/* ─── PROPUESTAS LOCALES ─── */}
        <div className={styles.sectionHeader} onClick={() => setLocalOpen(!localOpen)}>
          <span className={styles.sectionTitle}>Propuestas Agencias</span>
          {borradorCount > 0 && <span className={styles.sectionBadge}>{borradorCount}</span>}
          <span className={`${styles.sectionChevron} ${localOpen ? styles.open : ""}`}>&#9660;</span>
        </div>

        {localOpen && (
          <>
            <div className={styles.filterBar}>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por código, nombre, cliente, agencia..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={{ minWidth: 280 }}
              />
              <AgenciasDropdown
                value={filtroAgencia}
                onChange={(e) => setFiltroAgencia(e.target.value)}
                intermediarios={intermediarios}
                placeholder="Todas las agencias"
              />
              <select className="form-select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                <option value="">Todos los estados</option>
                <option value="borrador">Borrador</option>
                <option value="ejecutada">Ejecutada</option>
              </select>
            </div>

            {cotizacionesFiltradas.length === 0 ? (
              <div className={styles.empty}>
                {cotizaciones.length === 0
                  ? "No hay propuestas externas registradas."
                  : "No se encontraron propuestas con los filtros aplicados."}
              </div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.cotizacionesTable}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("created_at")}>Fecha{sortIndicator("created_at")}</th>
                      <th onClick={() => handleSort("codigo_generado")}>Código{sortIndicator("codigo_generado")}</th>
                      <th onClick={() => handleSort("_agencia_nombre")}>Agencia{sortIndicator("_agencia_nombre")}</th>
                      <th onClick={() => handleSort("_ejecutivo_nombre")}>
                        Ejecutivo{sortIndicator("_ejecutivo_nombre")}
                      </th>
                      <th onClick={() => handleSort("cliente_nombre")}>Cliente{sortIndicator("cliente_nombre")}</th>
                      <th onClick={() => handleSort("nombre_cotizacion")}>
                        Descripción{sortIndicator("nombre_cotizacion")}
                      </th>
                      <th onClick={() => handleSort("edificios_unicos_total")}>
                        Edif.{sortIndicator("edificios_unicos_total")}
                      </th>
                      <th onClick={() => handleSort("ascensores_seleccionados")}>
                        Pant.{sortIndicator("ascensores_seleccionados")}
                      </th>
                      <th onClick={() => handleSort("inversion_final_total")}>
                        Inversión{sortIndicator("inversion_final_total")}
                      </th>
                      <th onClick={() => handleSort("confianza_agencia")}>Conf.{sortIndicator("confianza_agencia")}</th>
                      <th onClick={() => handleSort("version")}>Ver.{sortIndicator("version")}</th>
                      <th onClick={() => handleSort("estado")}>Estado{sortIndicator("estado")}</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cotizacionesFiltradas.map((c) => (
                      <tr key={c.id}>
                        <td style={{ textAlign: "center" }}>{formatFecha(c.created_at)}</td>
                        <td style={{ textAlign: "center", fontWeight: 600 }}>{c.codigo_generado}</td>
                        <td title={c._agencia_nombre}>{c._agencia_nombre}</td>
                        <td title={c._ejecutivo_nombre}>{c._ejecutivo_nombre}</td>
                        <td>{c.cliente_nombre || <span style={{ color: "#999" }}>Sin asignar</span>}</td>
                        <td title={c.nombre_cotizacion}>{c.nombre_cotizacion || "-"}</td>
                        <td style={{ textAlign: "center" }}>{c.edificios_unicos_total || "-"}</td>
                        <td style={{ textAlign: "center" }}>
                          {(c.ascensores_seleccionados || 0) + (c.totems_seleccionados || 0) || "-"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {c.moneda_dolar
                            ? `USD ${formatNum(c.final_amount || c.inversion_final_total)}`
                            : `${monedaSymbol} ${formatNum(c.final_amount || c.inversion_final_total)}`}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {c.confianza_agencia ? (
                            <span className={`${styles.confianzaBadge} ${getConfianzaClass(c.confianza_agencia)}`}>
                              {c.confianza_agencia}
                            </span>
                          ) : (
                            <span style={{ color: "#ccc" }}>-</span>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span className={styles.versionBadge}>{c.version || 1}</span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span className={`${styles.estadoBadge} ${styles[c.estado || "borrador"]}`}>
                            {c.estado || "borrador"}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            className={`${styles.actionBtn} ${styles.pdfBtn}`}
                            title={edificiosData.length === 0 ? "Cargando edificios..." : "Descargar PDF"}
                            disabled={edificiosData.length === 0}
                            onClick={() => handleDownloadPdf(c)}
                          >
                            <BsDownload />
                          </button>
                          <button
                            className={`${styles.actionBtn} ${styles.formalizarBtn}`}
                            title={c.estado === "ejecutada" ? "Ya formalizada" : "Formalizar como oportunidad interna"}
                            disabled={c.estado === "ejecutada"}
                            onClick={() => setFormalizarTarget(c)}
                          >
                            <BsCheckCircle />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── MODAL FORMALIZAR ─── */}
      {formalizarTarget && (
        <FormalizarModal
          cotizacion={formalizarTarget}
          clientes={clientes}
          intermediarioNombre={formalizarTarget._agencia_nombre}
          tipoCotizacion={formalizarTarget._rol_creador === "agencia" ? "agencia" : "cliente"}
          pais={pais}
          monedaSymbol={monedaSymbol}
          formatNum={formatNum}
          onSubmit={handleFormalizar}
          onClose={() => setFormalizarTarget(null)}
        />
      )}
    </div>
  );
};

export default PropuestasExternas;
